import { useMemo, useRef, useState } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { X } from 'lucide-react'
import type { OriginalTrophy, LayoutItem } from '@/api/trophies'
import { toDragId, toSlotId } from '@/lib/shelf-layout'
import type { ShelfLayout } from '@/lib/shelf-layout'
import { TrophyFigure, EmptyPedestal, ShelfBoard } from './trophyShared'
import TrophyDetailDialog from './TrophyDetailDialog'
import TrophyPickerDialog from './TrophyPickerDialog'

const COLS = 3
const MIN_ROWS = 4  // 初期状態で 3×4 の空棚を表示

/** ドラッグ可能なトロフィー */
function DraggableTrophy({
  trophy, onRemove,
}: { trophy: OriginalTrophy; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: toDragId({ from: 'shelf', trophyId: trophy.id }),
  })
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`relative h-full touch-none ${isDragging ? 'opacity-30' : ''}`}
    >
      <TrophyFigure trophy={trophy} />
      <button
        type="button"
        onPointerDown={(e) => { e.stopPropagation() }}
        onClick={(e) => { e.stopPropagation(); onRemove() }}
        className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
      >
        <X size={12} />
      </button>
    </div>
  )
}

/** ドロップ先スロット（一覧からはいつでも落とせるよう、表示モードでも有効） */
function Slot({ pos, children }: { pos: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: toSlotId(pos) })
  return (
    <div ref={setNodeRef} className={`h-full rounded-md ${isOver ? 'bg-primary/10 ring-2 ring-primary/40' : ''}`}>
      {children}
    </div>
  )
}

/**
 * 自由棚。配置データと DndContext は親（TrophyTab）が持ち、ここはスロットとドラッグ要素を置く。
 */
export default function FreeShelf({
  trophies,
  layout,
  onApply,
  onDelete,
}: {
  trophies: OriginalTrophy[]
  layout: ShelfLayout
  onApply: (items: LayoutItem[]) => void
  onDelete?: (t: OriginalTrophy) => Promise<boolean>
}) {
  const [editing, setEditing] = useState(false)
  const [detail, setDetail] = useState<OriginalTrophy | null>(null)
  const [pickerSlot, setPickerSlot] = useState<number | null>(null)

  const trophyById = useMemo(
    () => Object.fromEntries(trophies.map((t) => [String(t.id), t])),
    [trophies],
  )
  const placedIds = useMemo(() => new Set(Object.keys(layout)), [layout])

  const slotTrophy: Record<number, string> = {}
  let maxPos = -1
  for (const [id, pos] of Object.entries(layout)) {
    slotTrophy[pos] = id
    if (pos > maxPos) maxPos = pos
  }
  const usedRows = Math.floor(maxPos / COLS) + 1
  const totalRows = Math.max(MIN_ROWS, usedRows + (editing ? 1 : 0))
  const capacity = totalRows * COLS

  const handleRemove = (id: string) => {
    onApply([{ id: Number(id), position: null }])
  }

  const handlePick = (t: OriginalTrophy) => {
    if (pickerSlot === null) return
    setPickerSlot(null)
    onApply([{ id: t.id, position: pickerSlot }])
  }

  // 配置トロフィーの長押しで編集モードへ（表示モード時）
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longFired = useRef(false)
  const startLong = () => {
    longFired.current = false
    longTimer.current = setTimeout(() => { longFired.current = true; setEditing(true) }, 500)
  }
  const clearLong = () => { if (longTimer.current) clearTimeout(longTimer.current) }

  const rows = Array.from({ length: Math.ceil(capacity / COLS) }, (_, r) =>
    Array.from({ length: COLS }, (_, c) => r * COLS + c),
  )

  const renderSlotContent = (pos: number) => {
    const id = slotTrophy[pos]
    if (id) {
      const t = trophyById[id]
      if (!t) return null
      if (editing) {
        return <DraggableTrophy trophy={t} onRemove={() => handleRemove(id)} />
      }
      return (
        <button
          type="button"
          className="h-full w-full"
          onPointerDown={startLong}
          onPointerUp={clearLong}
          onPointerLeave={clearLong}
          onClick={() => { if (!longFired.current) setDetail(t) }}
        >
          <TrophyFigure trophy={t} />
        </button>
      )
    }
    // 空きスロット
    if (!editing) {
      return (
        <div className="pointer-events-none flex h-full w-full items-end justify-center pb-1">
          <div className="h-9 w-9 rounded-full border border-muted-foreground/15" />
        </div>
      )
    }
    return (
      <button type="button" className="h-full w-full" onClick={() => setPickerSlot(pos)}>
        <EmptyPedestal editable />
      </button>
    )
  }

  const shelfGrid = (
    <div className="space-y-1">
      {rows.map((row, r) => (
        <div key={r}>
          <div className="grid min-h-[74px] grid-cols-3 items-end gap-1 px-2 pb-1">
            {row.map((pos) => (
              <Slot key={pos} pos={pos}>{renderSlotContent(pos)}</Slot>
            ))}
          </div>
          <ShelfBoard />
        </div>
      ))}
    </div>
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {editing ? 'ドラッグで移動・入れ替え／＋で追加' : 'タップで詳細・長押しで編集'}
        </p>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className={`rounded-full border px-3 py-1 text-xs ${editing ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`}
        >
          {editing ? '完了' : '編集'}
        </button>
      </div>

      <div
        className="rounded-lg border p-2"
        style={{
          background: 'linear-gradient(180deg,rgba(120,80,40,.10),rgba(80,50,25,.16))',
          borderColor: 'rgba(120,80,40,.25)',
        }}
      >
        {shelfGrid}
      </div>

      <TrophyPickerDialog
        open={pickerSlot !== null}
        candidates={trophies}
        placedIds={placedIds}
        onSelect={handlePick}
        onClose={() => setPickerSlot(null)}
      />
      <TrophyDetailDialog
        trophy={detail}
        onClose={() => setDetail(null)}
        onDelete={onDelete ? async (t) => { if (await onDelete(t)) setDetail(null) } : undefined}
      />
    </div>
  )
}
